import React, { useState, useEffect } from "react";
import moment from "moment";
import { css } from "emotion";
import jwtDecode from "jwt-decode";
import { getRebookingTransaction, getRebookingToken } from "../../../shared/requests-api";
import { Button, MaxModal, Input } from "@maxmilhas/hangar-components-atomic/public";
import DatePicker from "../../DatePicker";
import * as cs from "./styles";
import AirportAutoComplete from "../../MaxAirBridge/AirportAutoComplete";
import { getAirportDescriptionByIata, formatCurrency } from "../../../shared/utils";

const RebookingModal = ({ open, token, tripId, onClose }) => {
	const [loading, setLoading] = useState(true);
	const [actionLoading, setActionLoading] = useState(false);
	const [step, setStep] = useState(1);
	const [trip, setTrip] = useState(null);
	const [outboundDate, setOutboundDate] = useState(null);
	const [inboundDate, setInboundDate] = useState(null);
	const [onlyOutbound, setOnlyOutbound] = useState(false);
	const [selectedPassengers, setSelectedPassengers] = useState({
		adults: [],
		children: [],
		infants: []
	});
	const [numberOfPassengers, setNumberOfPassengers] = useState(0);
	const [modalError, setModalError] = useState({
		open: false,
		message:
			"Alguma coisa deu errado. Tente novamente ou contate nosso time de atendimento para solicitar ajuda.",
		title: "Oops!",
		actionButtonText: "Fechar"
	});
	const [flightOrigin, setFlightOrigin] = useState(null);
	const [flightDestination, setFlightDestination] = useState(false);
	const [fromAirportDescription, setFromAirportDescription] = useState(false);
	const [toAirportDescription, setToAirportDescription] = useState(false);

	useEffect(() => {
		const getTrip = async () => {
			const _trip = await getRebookingTransaction(token, tripId);

			Object.values(_trip.data.passengers).forEach(passengers => {
				passengers.forEach(passenger => {
					const credit = _trip.data.rebookingCredit.find(_credit => {
						const { id } = _credit.context.passenger;
						return Number(id) === Number(passenger.idpassengers);
					});

					passenger.creditBalance = credit ? credit.balance : 0;
				});
			});

			setTrip(_trip.data);

			await fillSuggestedFlightRoute(_trip.data);
			setLoading(false);
		};

		getTrip();
	}, []);

	useEffect(() => {
		if (trip) {
			let passengersCount = 0;

			Object.values(trip.passengers).forEach(passengers => {
				passengers.forEach(() => (passengersCount += 1));
			});

			setNumberOfPassengers(passengersCount);

			if (passengersCount === 1) {
				setSelectedPassengers(trip.passengers);
			}
		}
	}, [trip]);

	useEffect(() => {
		if (onlyOutbound) setInboundDate(null);
	}, [onlyOutbound]);

	const handleDate = (value, type) => {
		if (type === "outbound") {
			setOutboundDate(value ? moment(value).format("DD/MM/YYYY") : null);
		} else if (type === "inbound" && value) {
			setInboundDate(value ? moment(value).format("DD/MM/YYYY") : null);
		}
	};

	const setAirportsValue = data => {
		setFlightOrigin(data.outboundIata);
		setFlightDestination(data.inboundIata);
	};

	/**
	 * Fill the "from" "to" on the form based on the trip to be rescheduled
	 */
	const fillSuggestedFlightRoute = async trip => {
		const fromAirportDescription = await getAirportDescriptionByIata(trip.flights.outbound.from);
		const toAirportDescription = await getAirportDescriptionByIata(trip.flights.outbound.to);

		setFromAirportDescription(fromAirportDescription);
		setToAirportDescription(toAirportDescription);

		setFlightOrigin(trip.flights.outbound.from);
		setFlightDestination(trip.flights.outbound.to);
	};

	const handleOnlyOutbound = e => {
		setOnlyOutbound(e.target.checked);
	};

	const handleSelectPassenger = (type, passenger, checked) => {
		const passengersArr = [...selectedPassengers[type]];

		if (checked) {
			passengersArr.push(passenger);
		} else {
			const index = passengersArr.indexOf(passenger);
			passengersArr.splice(index, 1);
		}

		setSelectedPassengers({
			...selectedPassengers,
			[type]: passengersArr
		});
	};

	const getPassengerCredit = passenger => {
		return (trip.rebookingCredit || []).find(credit => {
			const pass = credit.context.passenger;
			return Number(pass.id) === Number(passenger.idpassengers);
		});
	};

	const handleDisablePassenger = (type, passenger) => {
		const credit = getPassengerCredit(passenger);
		return (credit || {}).status !== "AVAILABLE";
	};

	const calcNumberOfPassengers = (credits, type) =>
		credits.reduce((total, current) => {
			if (Number(current.context.passenger.type) === type) total += 1;
			return total;
		}, 0);

	const handleSearch = async () => {
		try {
			setActionLoading(true);
			const cabin = trip.flights.outbound.cabin;

			const idrebookingCredit = [];

			Object.values(selectedPassengers).forEach(passengers => {
				passengers.forEach(passenger => {
					const credit = getPassengerCredit(passenger);
					idrebookingCredit.push(credit.idrebookingCredit);
				});
			});

			const flightsDate = {
				outboundDepartureDate: moment(outboundDate, "DD/MM/YYYY").format("YYYY-MM-DD")
			};

			if (!onlyOutbound) {
				flightsDate.inboundDepartureDate = moment(inboundDate, "DD/MM/YYYY").format("YYYY-MM-DD");
			}

			const creditToken = await getRebookingToken(token, { idrebookingCredit, flightsDate });
			const decodedToken = jwtDecode(creditToken.data.rebookingToken);

			const searchObj = {
				adults: calcNumberOfPassengers(decodedToken.credits, 1),
				children: calcNumberOfPassengers(decodedToken.credits, 2),
				infants: calcNumberOfPassengers(decodedToken.credits, 3),
				cabin: !cabin || cabin !== "???" ? cabin : "EC",
				from: flightOrigin,
				to: flightDestination,
				outboundDate: moment(outboundDate, "DD/MM/YYYY").format("YYYY-MM-DD"),
				tripType: inboundDate ? "RT" : "OW",
				context: {
					airlines: [trip.flights.outbound.airline.toLowerCase()],
					token: creditToken.data.rebookingToken
				}
			};

			if (!onlyOutbound) {
				searchObj.inboundDate = moment(inboundDate, "DD/MM/YYYY").format("YYYY-MM-DD");
			}

			searchObj.context.providerType = trip.isOTA ? "revenue" : "miles";

			localStorage.setItem("contextualSearch", JSON.stringify(searchObj));
			window.location.pathname = "/voos-selecionados";
		} catch (error) {
			if (
				error.response &&
				(error.response.data.type === "MaxMilhas\\Rebooking\\UserGenderNotFoundException" ||
					error.response.data.type === "MaxMilhas\\Rebooking\\UserDocumentNotFoundException")
			) {
				const passengerName = error.response.data.error
					.match(/#[^\s]+/)
					.pop()
					.replace(/#/, "");
				setModalError({
					open: true,
					message: `Na data da nova viagem, o passageiro(a) <b>${passengerName}</b> terá mais de 12 anos e <b>passará a integrar a tarifa de adulto</b> na companhia aérea. Antes de seguir com a nova compra, entre em contato conosco pelo chat e solicite essa alteração no cadastro de passageiros da viagem.`,
					title: "Atenção",
					actionButtonText: "Ok, entendi."
				});
			} else if (error.message.search("bloqueado") !== -1) {
				setModalError({
					...modalError,
					open: true,
					message: "Você já utilizou os créditos para essa transação."
				});
			} else {
				setModalError({
					...modalError,
					open: true,
					message:
						"Alguma coisa deu errado. Tente novamente ou contate nosso time de atendimento para solicitar ajuda."
				});
			}
		}
	};

	const handleStep = () => {
		if (step === 1) {
			setStep(2);
		} else if (step === 2) {
			handleSearch();
		}
	};

	const handleMaxDate = () => {
		const airline = trip.flights.outbound.airline.toLowerCase();
		const orderDate = trip.order.purchaseDate;

		if (!trip.isOTA) {
			return moment(orderDate)
				.add(450, "days")
				.format("YYYY-MM-DD");
		}

		if (airline === "azul" || airline === "latam" || airline === "gol") {
			return moment(orderDate)
				.add(360, "days")
				.format("YYYY-MM-DD");
		}

		return moment()
			.add(330, "days")
			.format("YYYY-MM-DD");
	};

	return (
		<MaxModal
			open={open}
			onClose={actionLoading ? () => undefined : () => onClose(false)}
			closeOnOverlayClick={false}
			focusTrapped={false}
		>
			<div
				className={css`
					${cs.rebookingModal}
				`}
			>
				<img
					className="modal-thumbnail"
					src="https://website-hangar.s3.amazonaws.com/2020/02/14/protocol-cover.svg"
				/>

				<h2>Usar crédito em uma nova viagem</h2>

				{loading && (
					<div className="loader-container">
						<i className="loader loader-fetching center-block" />
					</div>
				)}

				{!loading && step === 1 && (
					<>
						<p>Confira algumas condições das companhias:</p>

						<ul>
							<li>
								<p>
									O uso do crédito é permitido apenas em voos com as{" "}
									<strong>mesmas regras tarifárias da passagem original.</strong>
								</p>
							</li>
							<li>
								<p>O crédito deve ser usado pelos mesmos passageiros da viagem original.</p>
							</li>
							<li>
								<p>
									O crédito poderá ser usado uma <strong>única vez por passageiro.</strong>
								</p>
							</li>
							<li>
								<p>
									Se o valor do novo voo for maior que o valor do voo original, essa diferença
									deverá ser paga normalmente.
								</p>
							</li>
							<li>
								<p>
									O novo voo só poderá ser reembolsado ou convertido em novo crédito caso haja
									alteração por parte da companhia.
								</p>
							</li>
						</ul>

						<p>
							Após preencher os dados da sua viagem, você será direcionado para uma lista de voos
							pré-selecionados de acordo com as regras da companhia e poderá escolher a sua passagem
							por lá.
						</p>
					</>
				)}

				{!loading && step === 2 && (
					<>
						<h3>Dados da nova viagem</h3>

						<div className="modal-row airport-autocomplete">
							<AirportAutoComplete
								outboundTextValue={fromAirportDescription}
								inboundTextValue={toAirportDescription}
								outboundValue={flightOrigin}
								inboundValue={flightDestination}
								outboundSelected={true}
								inboundSelected={true}
								getValue={data => setAirportsValue(data)}
								hasError={{
									from: false,
									to:
										flightOrigin === flightDestination
											? "O destino deve ser diferente da origem"
											: false
								}}
							/>
						</div>

						<div className="modal-row less-margin-bottom">
							<DatePicker
								id="data-de-ida"
								value={outboundDate}
								label="Data de ida *"
								icon="icon-calendar"
								minDate={moment()
									.add(5, "days")
									.format("YYYY-MM-DD")}
								maxDate={handleMaxDate()}
								onChange={value => handleDate(value, "outbound")}
							/>

							{!onlyOutbound && (
								<DatePicker
									id="data-de-volta"
									value={inboundDate}
									label="Data de volta"
									icon="icon-calendar"
									minDate={
										outboundDate ||
										moment()
											.add(5, "days")
											.format("YYYY-MM-DD")
									}
									maxDate={handleMaxDate()}
									onChange={value => handleDate(value, "inbound")}
								/>
							)}
						</div>

						<div className="modal-row no-margin-top">
							<Input
								checked={onlyOutbound}
								onChange={handleOnlyOutbound}
								type="checkbox"
								id="onlyOutbound"
								name="onlyOutbound"
								label="Apenas ida"
							/>
						</div>

						{numberOfPassengers > 1 && (
							<>
								<h3>Passageiros</h3>

								<div className="modal-row column">
									{Object.entries(trip.passengers).map(types => {
										const [type, passengers] = types;
										return passengers.map((passenger, index) => (
											<div key={`${passenger.name}-${index}`}>
												<Input
													type="checkbox"
													onChange={e => handleSelectPassenger(type, passenger, e.target.checked)}
													id={`${passenger.name.toLowerCase().replace(/ /g, "-")}-${index}`}
													name={`${passenger.name.toLowerCase().replace(/ /g, "-")}-${index}`}
													label={`${passenger.name}${
														passenger.suffix ? ` ${passenger.suffix}` : ""
													} - ${
														handleDisablePassenger(type, passenger)
															? "(Este passageiro não possui crédito)"
															: formatCurrency(passenger.creditBalance)
													}`}
													disabled={handleDisablePassenger(type, passenger)}
												/>
											</div>
										));
									})}
								</div>
							</>
						)}

						<div className="modal-row">
							<div className="modal-info">
								<i className="icon-max-communication-info" />

								<div>
									<p>
										Cada passageiro pode utilizar sua parte do crédito, mesmo que em momentos
										diferentes. Porém, o crédito de voos com bebês e crianças até 12 anos
										incompletos só pode ser utilizado junto ao passageiro adulto.{" "}
									</p>
								</div>
							</div>
						</div>
					</>
				)}

				{!loading && (
					<div className="modal-buttons">
						<div
							className={css`
								${cs.modalSteps(step, 2)}
							`}
						/>

						<Button
							onClick={handleStep}
							disabled={
								actionLoading ||
								(step === 2 &&
									(!outboundDate ||
										selectedPassengers.adults.length === 0 ||
										(!onlyOutbound && !inboundDate) ||
										!flightOrigin ||
										!flightDestination ||
										flightDestination === flightOrigin))
							}
						>
							{actionLoading && <i className="loader loader-fetching center-block" />}

							{!actionLoading && step === 1 && "Próximo"}
							{!actionLoading && step === 2 && "Buscar voo"}
						</Button>
					</div>
				)}

				{modalError.open && (
					<MaxModal
						open={modalError.open}
						onClose={() => (window.location.pathname = "/cliente/minhas-viagens-light")}
						styles={{ modal: { minWidth: 335, zIndex: 99999 } }}
					>
						<div
							className={css`
								${cs.rebookingErrorModal}
							`}
						>
							<h2 className="title">{modalError.title}</h2>
							<p
								dangerouslySetInnerHTML={{
									__html: modalError.message
								}}
							></p>
							<Button
								size="small"
								onClick={() => (window.location.pathname = "/cliente/minhas-viagens-light")}
							>
								{modalError.actionButtonText}
							</Button>
						</div>
					</MaxModal>
				)}
			</div>
		</MaxModal>
	);
};

export default RebookingModal;
